import React, { FC, useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Modal, message, Popconfirm, Button, Select, Input, Progress, notification } from "antd";
import { DownloadOutlined } from '@ant-design/icons';
import { getSeriesListApi, createSeriesApi, updateSeriesNameApi } from "../../service/series";
import { deleteFolderApi } from "../../service/folders";
import { getTaskListApi } from "../../service/folders";
import JSZip from 'jszip';
import { AlertMessage, SeriesCardSkeleton, MiniPaginator } from "../../components/common";
import { useAlert } from "../../hooks/useAlert";
import { ALERT_Z_INDEX } from "../../utils/alertManager";
import { getVideoPlayUrl } from "../../utils/videoUrl";
import "../../styles/reset.scss";
import "./Home.scss";
import { PermissionGuard } from "../../components/common/PermissionGuard";
import { RoleBasedComponent } from "../../components/common/RoleBasedComponent";
import EditIconExample from "../../components/common/EditIconExample";
import { globalStore } from "../../utils/globalStore";
import { getEnv } from "../../config/video";
import { getAllLanguages, getLanguageName, getLanguageCodeFromName } from "../../utils/languageUtils";
import { COPYRIGHT_TEXT } from "../../config/constants";
import { UserRoleManager } from "../../utils/userRole";
interface Props { }

export const Home: FC<Props> = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // 本地保存权限和菜单，避免初始渲染时读到空数据后不再更新
  const [userPermissions, setUserPermissions] = useState<string[]>(
    () => globalStore.getUserInfo()?.permissions || []
  );
  const [menuListState, setMenuListState] = useState<any[]>(
    () => globalStore.getMenuList() || []
  );

  // 订阅 globalStore 变化，保证权限/菜单更新能触发重渲染
  useEffect(() => {
    const unsubscribe = globalStore.subscribe(() => {
      const user = globalStore.getUserInfo();
      setUserPermissions(user?.permissions || []);
      setMenuListState(globalStore.getMenuList() || []);
    });
    return () => {
      unsubscribe();
    };
  }, []);
  
  // 根据用户角色初始化 ownerType（仅在组件挂载时执行一次）
  useEffect(() => {
    const currentRole = UserRoleManager.getCurrentUserRole();
    if (currentRole?.isUser) {
      setOwnerType(2); // 普通用户设置为普通用户剧集
    }
    // 非普通用户保持默认值1（团队剧集），不需要更新
  }, []); // 空依赖数组，仅在组件挂载时执行一次

  // 检查剧集按钮权限的函数（基于本地 state）
  const checkSeriesButtonPermission = useCallback(
    (permission: string): boolean => {
      // 超级管理员 / 管理员：视为拥有所有剧集相关按钮权限
      const currentRole = UserRoleManager.getCurrentUserRole();
      if (currentRole?.isAdmin) {
        return true;
      }

      // 先看 permissions 数组
      if (
        userPermissions &&
        userPermissions.length > 0 &&
        userPermissions.includes(permission)
      ) {
        return true;
      }

      // 再看菜单中的 meta.perms
      const menuList = menuListState;
      if (!menuList || menuList.length === 0) {
        return false;
      }

      const findMenuPermission = (menus: any[], targetPermission: string): boolean => {
        for (const menu of menus) {
          if (menu.meta?.perms === targetPermission) {
            return true;
          }
          if (menu.children && menu.children.length > 0) {
            if (findMenuPermission(menu.children, targetPermission)) {
              return true;
            }
          }
        }
        return false;
      };

      return findMenuPermission(menuList, permission);
    },
    [userPermissions, menuListState]
  );

  // 检查剧集按钮管理权限
  const hasSeriesButtonManagePermission = (): boolean => {
    return checkSeriesButtonPermission('seriesBtn:manage');
  };

  // 检查剧集删除权限
  const hasSeriesDeletePermission = (): boolean => {
    return checkSeriesButtonPermission('series:delete');
  };

  // 检查剧集下载权限（后端权限码为 series:download）
  const hasSeriesDownloadPermission = (): boolean => {
    return checkSeriesButtonPermission('series:download');
  };

  // 检查图钉操作权限
  const hasPinOperationPermission = (): boolean => {
    return checkSeriesButtonPermission('seriesBtn:pin');
  };

  // 检查创建剧集权限
  const hasCreateSeriesPermission = (): boolean => {
    return checkSeriesButtonPermission('system:series:create');
  };

  // 检查是否是管理员或超级管理员
  const isAdminOrSuperAdmin = (): boolean => {
    const currentRole = UserRoleManager.getCurrentUserRole();
    if (currentRole?.isAdmin) {
      return true;
    }
    // 额外检查：直接从用户信息中检查角色key
    const userInfo = globalStore.getUserInfo();
    if (userInfo?.roles) {
      const roleKeys = userInfo.roles.map((role: any) => 
        (typeof role === 'string' ? role : role.role_key || role.code || '').toLowerCase()
      );
      return roleKeys.some((key: string) => key === 'super_admin' || key === 'admin' || key === 'administrator');
    }
    return false;
  };
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [sortBy, setSortBy] = useState<'time' | 'alphabet'>('time');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInputValue, setSearchInputValue] = useState(''); // 用于输入框显示，立即更新
  
  // 根据用户角色设置默认的 ownerType：普通用户默认为2（普通用户剧集），其他角色默认为1（团队剧集）
  const getDefaultOwnerType = (): number => {
    const currentRole = UserRoleManager.getCurrentUserRole();
    if (currentRole?.isUser) {
      return 2; // 普通用户默认显示普通用户剧集
    }
    return 1; // 其他角色默认显示团队剧集
  };
  
  const [ownerType, setOwnerType] = useState<number>(getDefaultOwnerType()); // 剧集拥有者类型：1代表团队，2代表普通用户剧集
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null); // 防抖定时器
  const { alertConfig, hideAlert, showSuccess, showError, showInfo } = useAlert();
  
  // 使用 notification Hook
  const [api, contextHolder] = notification.useNotification();

  // 根据屏幕尺寸动态调整分页大小
  useEffect(() => {
    const updatePageSize = () => {
      const width = window.innerWidth;
      let newPageSize = 15;

      if (width <= 400) {
        newPageSize = 2;
      } else if (width <= 600) {
        newPageSize = 4;
      } else if (width <= 800) {
        newPageSize = 6;
      } else if (width <= 1000) {
        newPageSize = 8;
      } else if (width <= 1200) {
        newPageSize = 10;
      } else if (width <= 1600) {
        newPageSize = 15;
      } else {
        newPageSize = 15;
      }

      if (newPageSize !== pageSize) {
        setPageSize(newPageSize);
        setCurrentPage(1);
      }
    };

    updatePageSize();
    window.addEventListener('resize', updatePageSize);

    return () => {
      window.removeEventListener('resize', updatePageSize);
    };
  }, [pageSize]);

  const [apiData, setApiData] = useState<Task.GetTaskListResult>({
    data: [],
    pagination: {
      page: 1,
      per_page: 10,
      total: 0,
      pages: 0,
      has_next: false,
      has_prev: false,
      sort: "alphabet"
    }
  });
  const [isLoading, setIsLoading] = useState(true);

  // 重命名相关状态
  const [renameModal, setRenameModal] = useState<{
    visible: boolean;
    item: any;
    newName: string;
  }>({
    visible: false,
    item: null,
    newName: ''
  });

  // 双击编辑相关状态
  const [editingItems, setEditingItems] = useState<Set<number>>(new Set());
  const [editingNames, setEditingNames] = useState<Record<number, string>>({});

  // 创建剧集相关状态
  const [createSeriesModal, setCreateSeriesModal] = useState<{
    visible: boolean;
    seriesName: string;
    sourceLanguage: string;
    subtitleErase: boolean;
  }>({
    visible: false,
    seriesName: '',
    sourceLanguage: '中文',
    subtitleErase: false
  });

  // 底部操作按钮状态
  const [actionMenu, setActionMenu] = useState<{
    visible: boolean;
    seriesId: number | null;
  }>({
    visible: false,
    seriesId: null
  });

  // 监听全局点击事件，关闭操作菜单
  useEffect(() => {
    const handleGlobalClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // 如果点击的不是操作按钮或操作菜单，则关闭菜单
      if (!target.closest('.action-btn.pin-btn') && !target.closest('.action-bubble-menu')) {
        closeActionMenu();
      }
    };

    if (actionMenu.visible) {
      document.addEventListener('click', handleGlobalClick);
    }

    return () => {
      document.removeEventListener('click', handleGlobalClick);
    };
  }, [actionMenu.visible]);

  const refresh = useCallback(async () => {
    try {
      setIsLoading(true);

      // 构建请求参数
      const params: Series.GetSeriesListParams = {
        pageNo: currentPage,
        pageSize: pageSize,
        ownerType: ownerType || 1, // 剧集拥有者类型：1代表团队，2代表散户，默认值为1
      };
      
      // 排序参数：1.时间 2.名称
      const sortMap: Record<'time' | 'alphabet', number> = {
        'time': 1,
        'alphabet': 2,
      };
      params.sort = sortMap[sortBy] || 1;
      
      // 如果有搜索关键词，添加 name 参数（绑定到搜索框）
      if (searchQuery.trim()) {
        params.name = searchQuery.trim();
      }

      const seriesData = await getSeriesListApi(params);

      // 适配新接口返回结构：{ code, data: { list, total }, msg }
      const list = seriesData.data?.list || [];
      const total = seriesData.data?.total || 0;

      // 保留接口返回的所有字段，同时兼容现有结构
      const convertedTasks = list.map(item => {
        // 后端返回的是 seriesId，这里统一适配为 id / series_id，便于后续使用
        const finalSeriesId = (item as any).seriesId ?? (item as any).id;

        return {
          name: item.name,
          id: finalSeriesId,
          series_id: finalSeriesId,
          // 也保留原始的 seriesId 字段，方便调试和兼容
          seriesId: finalSeriesId,
        // 保留接口返回的字段
          languageCount: item.languageCount,
          needRemovalSubtitle: item.needRemovalSubtitle ?? item.need_removal_subtitle ?? false,
          sourceLangCode: item.sourceLangCode ?? item.source_lang ?? null,
        // 其他字段保持为空或默认，用于兼容现有结构
        disable_bgm: false,
        has_user_srt: false,
        has_user_script: false,
        tag: 0,
        descript: null,
        user_id: 0,
        status: 2,
        create_time: "",
        update_time: "",
        file_url: null,
        source_hls_url: null,
        result_url: null,
        translated_subtitle_url: null,
        subtitle_url: null,
        pure_video_url: null,
        pure_video_hls_url: null,
        cover_url: null,
          backgroud_audio_url: null,
          script_url: null,
          sourceLanguage: item.sourceLangCode ?? item.source_lang ?? "",
          targetLanguage: item.target_lang ?? "",
          episodeTasks: []
        };
      });

      setApiData({
        data: convertedTasks,
        pagination: {
          page: currentPage,
          per_page: pageSize,
          total,
          pages: Math.ceil(total / pageSize),
          has_next: currentPage * pageSize < total,
          has_prev: currentPage > 1,
          sort: sortBy
        }
      });
    } catch (error) {
      message.error('获取数据失败');
      setApiData({
        data: [],
        pagination: {
          page: 1,
          per_page: 10,
          total: 0,
          pages: 0,
          has_next: false,
          has_prev: false,
          sort: "alphabet"
        }
      });
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, searchQuery, sortBy, ownerType]);

  // 同步 searchQuery 到 searchInputValue（防御性处理，确保状态一致）
  useEffect(() => {
    // 只有在 searchQuery 不是由用户输入触发时才同步
    // 如果存在防抖定时器，说明用户正在输入，不进行同步
    if (!debounceTimerRef.current) {
      setSearchInputValue(searchQuery);
    }
  }, [searchQuery]);

  // 监听路由变化、搜索关键词或排序方式变化，重新获取数据
  useEffect(() => {
    refresh();
  }, [location.pathname, currentPage, pageSize, searchQuery, sortBy, ownerType, refresh]);

  // 进入Home页后输出当前环境到控制台（基于构建时的环境变量）
  useEffect(() => {
    const currentEnv = getEnv();
    console.log('当前环境:', currentEnv, '(基于构建时的 MODE 环境变量)');
  }, []);

  // 检查登录成功标记并显示提示
  useEffect(() => {
    const loginSuccess = localStorage.getItem("loginSuccess");
    const loginSuccessMessage = localStorage.getItem("loginSuccessMessage");
    
    if (loginSuccess === "true" && loginSuccessMessage) {
      showSuccess(loginSuccessMessage);
      // 清除标记
      localStorage.removeItem("loginSuccess");
      localStorage.removeItem("loginSuccessMessage");
    }
  }, [showSuccess]);

  // 准备数据
  const seriesItems = (apiData.data && apiData.data.length > 0)
    ? apiData.data.map(series => ({
      ...series,
      type: 'series' as const,
      createTime: series.create_time,
      updateTime: series.update_time,
      userId: series.user_id,
      parentId: 0,
      // 保留接口返回的语言信息和字幕擦除状态
      sourceLanguage: series.sourceLanguage || (series as any).sourceLangCode || '',
      targetLanguage: series.targetLanguage || '',
      needRemovalSubtitle: series.needRemovalSubtitle || false
    }))
    : [];

  // 使用后端返回的分页数据（搜索和排序已由后端处理）
  const totalItems = apiData.pagination.total;
  const totalPages = apiData.pagination.pages;
  const currentPageItems = seriesItems;

  // 新接口不再展示语言标签，这里保留占位函数以兼容可能的引用
  const getTargetLanguageLabel = (targetLang: string): string => {
    return '';
  };

  // 处理剧集点击，跳转到剧集内层页面
  const handleSeriesClick = (series: any) => {
    // 需求调整：点击剧集进入剧集内层页面，而不是视频列表页
    // 优先使用适配后的 id / series_id，其次回退到原始 seriesId
    const finalSeriesId =
      series.id ??
      series.series_id ??
      series.seriesId ??
      null;

    if (!finalSeriesId) {
      message.warning('剧集ID不存在，无法打开剧集详情');
      return;
    }

    navigate(`/seriesListIn?seriesId=${finalSeriesId}&seriesName=${encodeURIComponent(series.name || '')}`);
  };

  const deleteSeries = async (seriesId: number, seriesName: string) => {
    try {
      await deleteFolderApi(seriesId);
      showSuccess(`剧集 "${seriesName}" 删除成功`);
      await refresh();
    } catch (error) {
      showError('删除剧集失败，请稍后重试');
    }
  };

  // 底部操作按钮事件处理
  const handleActionButtonClick = (seriesId: number) => {
    setActionMenu({
      visible: !actionMenu.visible,
      seriesId: actionMenu.visible ? null : seriesId
    });
  };

  // 关闭操作菜单
  const closeActionMenu = () => {
    setActionMenu({
      visible: false,
      seriesId: null
    });
  };

  // 菜单操作处理
  const handleMenuAction = (action: 'rework' | 'approve') => {
    if (!actionMenu.seriesId) return;
    
    const series = apiData.data?.find(item => item.id === actionMenu.seriesId);
    if (!series) return;

    if (action === 'rework') {
      // 需返工操作
      message.info(`剧集"${series.name}"标记为需返工`);
      // TODO: 调用API更新剧集状态
    } else if (action === 'approve') {
      // 审核通过操作
      message.success(`剧集"${series.name}"审核通过`);
      // TODO: 调用API更新剧集状态
    }
    
    closeActionMenu();
  };

  // 批量下载剧集内所有视频（纯前端实现）
  const handleBatchDownload = async (series: any) => {
    // 创建通知key
    const notificationKey = `download-progress-${Date.now()}`;
    
    const updateProgress = (progress: number, text: string) => {
      api.open({
        key: notificationKey,
        message: '下载进度',
        description: (
          <div>
            <div style={{ marginBottom: '8px', fontSize: '14px' }}>{text}</div>
            <Progress 
              percent={progress} 
              size="small" 
              status={progress === 100 ? 'success' : 'active'}
              strokeColor="#1890ff"
              showInfo={false}
            />
          </div>
        ),
        icon: <DownloadOutlined style={{ color: '#1890ff' }} />,
        duration: 0,
        placement: 'topRight',
        style: {
          width: 320,
          minHeight: 80
        },
        className: 'download-progress-notification'
      });
    };
    
    try {
      // 立即显示初始进度
      updateProgress(0, '正在获取视频列表...');
      
      // 获取该剧集下的所有视频
      // 这里语言编码暂时使用 'en'，用于统计该剧集中等待中的视频数量
      const videoListResponse = await getTaskListApi({ seriesId: series.id, langCode: 'en' });
      const completedVideos = videoListResponse.data.filter(video => 
        video.status === 2 && getVideoPlayUrl(video)
      );
      
      if (completedVideos.length === 0) {
        api.destroy(notificationKey);
        showError('该剧集下没有可下载的已完成视频');
        return;
      }
      
      // 更新进度，显示找到的视频数量
      updateProgress(5, `找到 ${completedVideos.length} 个可下载视频，准备开始下载...`);
      
      // 短暂延迟让用户看到进度更新
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // 创建JSZip实例
      const zip = new JSZip();
      const downloadPromises = [];
      let successCount = 0;
      let failCount = 0;
      
      // 为每个视频创建下载任务
      for (let i = 0; i < completedVideos.length; i++) {
        const video = completedVideos[i];
        const videoUrl = getVideoPlayUrl(video);
        
        if (videoUrl) {
          const downloadPromise = fetch(videoUrl)
            .then(response => {
              if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
              }
              return response.blob();
            })
            .then(blob => {
              // 生成安全的文件名
              const safeFileName = video.name.replace(/[<>:"/\\|?*]/g, '_');
              const fileName = `${safeFileName}.mp4`;
              zip.file(fileName, blob);
              successCount++;
              
              // 更新进度 - 下载阶段占85%（5%-90%）
              const downloadProgress = 5 + Math.round(((i + 1) / completedVideos.length) * 85);
              updateProgress(downloadProgress, `正在下载: ${video.name} (${i + 1}/${completedVideos.length})`);
            })
            .catch(error => {
              console.error(`下载视频 ${video.name} 失败:`, error);
              failCount++;
              
              // 即使失败也要更新进度
              const downloadProgress = 5 + Math.round(((i + 1) / completedVideos.length) * 85);
              updateProgress(downloadProgress, `下载失败: ${video.name} (${i + 1}/${completedVideos.length})`);
            });
          
          downloadPromises.push(downloadPromise);
        }
      }
      
      // 等待所有下载完成
      await Promise.allSettled(downloadPromises);
      
      if (successCount === 0) {
        api.destroy(notificationKey);
        showError('所有视频下载失败，请检查网络连接');
        return;
      }
      
      // 更新进度到压缩阶段
      updateProgress(90, '正在生成压缩包...');
      
      // 生成压缩包
      const zipBlob = await zip.generateAsync({ 
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: {
          level: 6
        }
      });
      
      // 更新进度到完成
      updateProgress(100, '压缩包生成完成，正在下载...');
      
      // 短暂延迟让用户看到100%进度
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // 创建下载链接
      const link = document.createElement('a');
      link.href = URL.createObjectURL(zipBlob);
      
      // 生成安全的文件名
      const safeSeriesName = series.name.replace(/[<>:"/\\|?*]/g, '_');
      link.download = `${safeSeriesName}_全集.zip`;
      link.style.display = 'none';
      
      // 触发下载
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // 清理URL对象
      URL.revokeObjectURL(link.href);
      
      // 关闭通知
      api.destroy(notificationKey);
      
      // 显示最终结果
      if (failCount > 0) {
        showInfo(`下载完成！成功下载 ${successCount} 个视频，${failCount} 个视频下载失败`);
      } else {
        showSuccess(`剧集 "${series.name}" 下载完成，共 ${successCount} 个视频`);
      }
      
    } catch (error) {
      api.destroy(notificationKey);
      showError('批量下载失败，请稍后重试');
      console.error('批量下载错误:', error);
    }
  };

  const handleSortChange = (value: 'time' | 'alphabet') => {
    setSortBy(value);
    setCurrentPage(1); // 切换排序时重置到第一页
  };

  // 搜索输入处理函数（带防抖优化）
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // 立即更新输入框显示的值，保证UI响应流畅
    setSearchInputValue(value);
    
    // 清除之前的定时器
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    // 设置新的防抖定时器，延迟更新 searchQuery 触发API调用
    debounceTimerRef.current = setTimeout(() => {
      setSearchQuery(value);
      setCurrentPage(1); // 搜索时重置到第一页
      debounceTimerRef.current = null;
    }, 500); // 防抖延迟500ms
  };

  // 组件卸载时清除定时器
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // 双击编辑相关函数
  const handleNameEditStart = (e: React.MouseEvent, itemId: number, currentName: string) => {
    e.stopPropagation();
    setEditingItems(prev => new Set(prev).add(itemId));
    setEditingNames(prev => ({ ...prev, [itemId]: currentName }));
  };

  const handleNameEditChange = (itemId: number, newName: string) => {
    setEditingNames(prev => ({ ...prev, [itemId]: newName ?? '' }));
  };

  const handleNameEditSave = async (itemId: number) => {
    const newName = editingNames[itemId];
    if (!newName || !newName.trim()) {
      setEditingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(itemId);
        return newSet;
      });
      setEditingNames(prev => {
        const newNames = { ...prev };
        delete newNames[itemId];
        return newNames;
      });
      return;
    }

    // 获取原始名字
    const originalItem = apiData.data.find(series => series.id === itemId);
    const originalName = originalItem?.name || '';
    const trimmedNewName = newName.trim();

    // 如果名字没有变化，直接退出编辑状态，不发送请求
    if (originalName === trimmedNewName) {
      setEditingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(itemId);
        return newSet;
      });
      setEditingNames(prev => {
        const newNames = { ...prev };
        delete newNames[itemId];
        return newNames;
      });
      return;
    }

    try {
      const res = await updateSeriesNameApi({
        series_id: itemId,
        name: trimmedNewName
      });

      if (res.code === 0 && res.data) {
        // 本地直接更新名称，避免整列表重新渲染卡顿
        setApiData(prev => ({
          ...prev,
          data: prev.data.map(series =>
            series.id === itemId ? { ...series, name: trimmedNewName } : series
          )
        }));
        showSuccess('重命名成功');
        
        // 成功时退出编辑状态
        setEditingItems(prev => {
          const newSet = new Set(prev);
          newSet.delete(itemId);
          return newSet;
        });
        setEditingNames(prev => {
          const newNames = { ...prev };
          delete newNames[itemId];
          return newNames;
        });
      } else {
        // 重命名失败：退出编辑状态，恢复原名称
        showError(res.msg || '重命名失败，请稍后重试');
        
        // 恢复原名称
        setApiData(prev => ({
          ...prev,
          data: prev.data.map(series =>
            series.id === itemId ? { ...series, name: originalName } : series
          )
        }));
        
        // 退出编辑状态
        setEditingItems(prev => {
          const newSet = new Set(prev);
          newSet.delete(itemId);
          return newSet;
        });
        setEditingNames(prev => {
          const newNames = { ...prev };
          delete newNames[itemId];
          return newNames;
        });
      }
    } catch (error: any) {
      // 重命名失败：退出编辑状态，恢复原名称
      showError(error?.message || '重命名失败，请稍后重试');
      
      // 恢复原名称
      setApiData(prev => ({
        ...prev,
        data: prev.data.map(series =>
          series.id === itemId ? { ...series, name: originalName } : series
        )
      }));
      
      // 退出编辑状态
      setEditingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(itemId);
        return newSet;
      });
      setEditingNames(prev => {
        const newNames = { ...prev };
        delete newNames[itemId];
        return newNames;
      });
    }
  };

  // 分页处理函数
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // 处理重命名
  const handleRename = async () => {
    if (!renameModal.item || !renameModal.newName.trim()) {
      return;
    }

    const originalName = renameModal.item.name || '';
    const trimmedNewName = renameModal.newName.trim();

    // 如果名字没有变化，直接关闭弹窗，不发送请求
    if (originalName === trimmedNewName) {
      setRenameModal({ visible: false, item: null, newName: '' });
      return;
    }

    try {
      const res = await updateSeriesNameApi({
        series_id: renameModal.item.id,
        name: trimmedNewName
      });

      if (res.code === 0 && res.data) {
        // 本地直接更新名称
        setApiData(prev => ({
          ...prev,
          data: prev.data.map(series =>
            series.id === renameModal.item.id ? { ...series, name: trimmedNewName } : series
          )
        }));
        showSuccess('重命名成功');
      } else {
        showError(res.msg || '重命名失败，请稍后重试');
      }

      setRenameModal({ visible: false, item: null, newName: '' });
    } catch (error: any) {
      showError(error?.message || '重命名失败，请稍后重试');
    }
  };

  // 创建剧集
  const createSeries = async () => {
    const { seriesName, sourceLanguage, subtitleErase } = createSeriesModal;

    if (!seriesName.trim()) {
      showError('请输入剧集名称');
      return;
    }

    // 移除同名剧集校验，允许同名剧集
    // const existingSeries = apiData.data.find(series =>
    //   series.name.toLowerCase() === seriesName.toLowerCase()
    // );
    // 
    // if (existingSeries) {
    //   showError(`剧集 "${seriesName}" 已存在，请使用其他名称`);
    //   return;
    // }

    try {
      message.loading('正在创建剧集...', 0);

      const sourceLang = getLanguageCode(sourceLanguage);

      const response = await createSeriesApi({
        name: seriesName.trim(),
        sourceLangCode: sourceLang,
        needRemovalSubtitle: subtitleErase
      });

      // 检查响应状态码
      if (response && response.code !== 0) {
        message.destroy();
        showError(response.msg || '创建剧集失败，请稍后重试');
        return;
      }

      message.destroy();
      showSuccess('剧集创建成功');
      setCreateSeriesModal({ visible: false, seriesName: '', sourceLanguage: '中文', subtitleErase: false });

      const newSeriesId = response?.data;
      if (typeof newSeriesId === 'number') {
        const encodedName = encodeURIComponent(seriesName.trim());
        navigate(`/videoList?seriesId=${newSeriesId}&seriesName=${encodedName}&isPure=true`);
      } else {
        refresh();
      }
    } catch (error: any) {
      message.destroy();
      // 优先使用返回数据的 msg，其次使用 error.message，最后使用默认提示
      const errorMessage = error?.response?.data?.msg || error?.response?.msg || error?.data?.msg || error?.msg || error?.message || '创建剧集失败，请稍后重试';
      showError(errorMessage);
    }
  };

  // 将语言名称转换为API需要的语言代码（使用统一的工具函数）
  const getLanguageCode = (languageName: string): string => {
    return getLanguageCodeFromName(languageName);
  };

  return (
    <>
      {contextHolder}
      <div className="main-content">
        <AlertMessage
          config={alertConfig}
          onClose={hideAlert}
          zIndex={ALERT_Z_INDEX.MY_WORK}
        />

        {/* 搜索和操作栏 */}
        <div className="search-filter-bar">
          <div className="search-sort-container">
            <div className="search-section">
              <div className="search-bar">
                <i className="fas fa-search search-icon"></i>
                <Input
                  placeholder="搜索剧集"
                  className="search-input show"
                  value={searchInputValue}
                  onChange={handleSearchChange}
                />
              </div>
            </div>

            <div className="sort-section">
              <Select
                value={sortBy}
                onChange={handleSortChange}
                className="sort-dropdown"
                placeholder="排序方式"
              >
                <Select.Option value="time">按时间排序</Select.Option>
                <Select.Option value="alphabet">按名称排序</Select.Option>
              </Select>
            </div>

            {/* 团队剧集下拉选择器 - 仅管理员和超级管理员可见 */}
            {isAdminOrSuperAdmin() && (
              <div className="sort-section">
                <Select
                  value={ownerType}
                  onChange={(value) => {
                    setOwnerType(value);
                    setCurrentPage(1); // 切换时重置到第一页
                  }}
                  className="sort-dropdown"
                  placeholder="剧集显示方式"
                >
                  <Select.Option value={1}>团队剧集</Select.Option>
                  <Select.Option value={2}>普通用户剧集</Select.Option>
                </Select>
              </div>
            )}
          </div>

          <div className="action-section">
            {/* 新建剧集按钮 - 需要system:series:create权限 */}
            {hasCreateSeriesPermission() && (
              <Button
                type="primary"
                className="new-episode-btn"
                onClick={() => setCreateSeriesModal({ ...createSeriesModal, visible: true })}
              >
                <i className="fas fa-plus"></i>
                新建剧集
              </Button>
            )}
          </div>
        </div>

        {isLoading ? (
          <div className="episodes-grid">
            <SeriesCardSkeleton count={pageSize} />
          </div>
        ) : currentPageItems.length === 0 ? (
          <div className="empty-search-container">
            <div className="empty-search-content">
              <div className="empty-icon">
                <i className="fas fa-folder"></i>
              </div>
              <div className="empty-text">
                暂无剧集
              </div>
            </div>
          </div>
        ) : (
          <div className="episodes-grid">
            {currentPageItems.map((item, index) => (
              <div key={`${item.type}-${item.id ?? index}`} className="episode-card-wrapper">
                <div className="episode-card">
                {/* 右上角语言标签已去掉，不再显示 */}

                {/* 可点击区域 */}
                <div 
                  className="clickable-area" 
                  data-tag={Number(item.tag ?? 0)}
                  onClick={() => handleSeriesClick(item)}
                  title={item.name}
                >
                  <div className="folder-icon">
                    <i className="fas fa-folder"></i>
                  </div>
                </div>

                {/* 剧集名称和编辑图标 */}
                <div className="episode-name-section">
                  {editingItems.has(item.id) ? (
                    <Input
                      value={editingNames[item.id] ?? ''}
                      onChange={(e) => handleNameEditChange(item.id, e.target.value)}
                      onPressEnter={() => handleNameEditSave(item.id)}
                      onBlur={() => handleNameEditSave(item.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                          setEditingItems(prev => {
                            const newSet = new Set(prev);
                            newSet.delete(item.id);
                            return newSet;
                          });
                          setEditingNames(prev => {
                            const newNames = { ...prev };
                            delete newNames[item.id];
                            return newNames;
                          });
                        }
                      }}
                      autoFocus
                      className="name-edit-input"
                    />
                  ) : (
                    <div className="name-with-edit">
                      <span
                        onDoubleClick={(e) => handleNameEditStart(e, item.id, item.name)}
                        className="name-text"
                        title={item.name}
                      >
                        {item.name}
                      </span>
                      <Button
                        className="edit-icon-btn"
                        onClick={(e) => handleNameEditStart(e, item.id, item.name)}
                        title="重命名"
                      >
                        <i className="fas fa-edit"></i>
                      </Button>
                    </div>
                  )}
                </div>

                {/* 底部操作按钮（仅保留删除） */}
                <div className="episode-actions">
                  {/* 剧集删除按钮 - 需要series:delete权限 */}
                  {hasSeriesDeletePermission() && (
                    <Popconfirm
                      title="确定要删除这个剧集吗？"
                      onConfirm={() => deleteSeries(item.id, item.name)}
                      okText="确定"
                      cancelText="取消"
                    >
                      <Button
                        className="action-btn delete-btn"
                        title="删除"
                      >
                        <i className="fas fa-trash"></i>
                      </Button>
                    </Popconfirm>
                  )}
                </div>
                </div>
                
                {/* 操作气泡菜单 - 在episode-card外面 */}
                {actionMenu.visible && actionMenu.seriesId === item.id && (
                  <div className="action-bubble-menu">
                    <div className="action-menu-content">
                      <div className="action-item" onClick={() => handleMenuAction('rework')}>
                        需返工
                      </div>
                      <div className="action-item" onClick={() => handleMenuAction('approve')}>
                        审核通过
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* 重命名模态框 */}
        <Modal
          title="重命名剧集"
          open={renameModal.visible}
          onOk={handleRename}
          onCancel={() => setRenameModal({ visible: false, item: null, newName: '' })}
          okText="确定"
          cancelText="取消"
        >
          <div>
            <Input
              value={renameModal.newName}
              onChange={(e) => setRenameModal(prev => ({ ...prev, newName: e.target.value }))}
              placeholder="请输入剧集名称"
              autoFocus
            />
          </div>
        </Modal>

        {/* 创建剧集模态框 */}
        <Modal
          title="新建剧集"
          open={createSeriesModal.visible}
          onOk={createSeries}
          onCancel={() => setCreateSeriesModal({
            visible: false,
            seriesName: '',
            sourceLanguage: '中文',
            subtitleErase: false
          })}
          okText="创建"
          cancelText="取消"
          zIndex={1000}
          destroyOnClose
        >
          <div className="create-episode-form">
            <div className="form-item">
              <label>剧集名称</label>
              <Input
                value={createSeriesModal.seriesName}
                onChange={(e) => setCreateSeriesModal(prev => ({
                  ...prev,
                  seriesName: e.target.value
                }))}
                placeholder="请输入剧集名称"
                autoFocus
              />
            </div>

            <div className="form-item">
                <label>源语言</label>
                <Select
                  value={createSeriesModal.sourceLanguage}
                  onChange={(value) => setCreateSeriesModal(prev => ({
                    ...prev,
                    sourceLanguage: value
                  }))}
                  placeholder="请选择源语言"
                  style={{ width: '100%' }}
                  dropdownStyle={{ zIndex: 1001 }}
                  getPopupContainer={(triggerNode) => triggerNode.parentNode}
                >
                  <Select.Option key="zh" value="中文">中文</Select.Option>
                </Select>
            </div>

            <div className="form-item">
              <div className="subtitle-toggle-wrapper">
                <label>字幕擦除</label>
                <div
                  className={`subtitle-toggle ${createSeriesModal.subtitleErase ? 'active' : ''}`}
                  onClick={() => setCreateSeriesModal(prev => ({
                    ...prev,
                    subtitleErase: !prev.subtitleErase
                  }))}
                >
                  <div className="toggle-handle"></div>
                </div>
              </div>
            </div>
          </div>
        </Modal>

        {/* 小型分页器 - 有数据时才显示 */}
        {apiData.pagination.total > 0 && apiData.pagination.pages > 0 && (
          <MiniPaginator
            page={apiData.pagination.page}
            pages={apiData.pagination.pages}
            hasPrev={apiData.pagination.has_prev}
            hasNext={apiData.pagination.has_next}
            onChange={handlePageChange}
          />
        )}
      {/* <EditIconExample /> */}
      
        {/* 版权信息 */}
        <div className="copyright-section">
          <div className="copyright-text">
            {COPYRIGHT_TEXT}
          </div>
        </div>
      </div>

    </>
  );
};
